import { SCALE } from '../../constants';
import { Box, Grid, withStyles } from '@material-ui/core';
import React, { useMemo } from 'react';

const Item = withStyles({
  root: {
    paddingLeft: `${16 * SCALE}px`,
    borderRight: '1px solid #CCCCCC',
    fontFamily: 'OpenSans',
    '& .title': {
      color: '#666',
      fontWeight: 600,
      fontSize: 10 * SCALE,
    },
    '& .value': {
      fontFamily: 'Lato',
      marginTop: `${4 * SCALE}px`,
      color: '#333',
      fontWeight: 600,
      fontSize: 18 * SCALE,
      lineHeight: `${24 * SCALE}px`,
    },
    '& .subValue': {
      fontFamily: 'OpenSans',
      marginLeft: `${8 * SCALE}px`,
      color: '#333',
      fontWeight: 600,
      fontSize: 12 * SCALE,
      lineHeight: `${18 * SCALE}px`,
    },
    '&:last-child': {
      borderRight: 'none',
    },
  },
})(Grid);

/**
 * Reusable Repeat Participation block (matched participants YoY).
 * @param {object} data - Metric object with { repeatParticipantTrendings: [{ year, percent, total, ... }], totalRepeatParticipant }
 */
function formatYearsSummary(n, years) {
  const participantStr = `participant${n !== 1 ? 's' : ''}`;
  const prefix = `A matched group of ${n} ${participantStr} attended the screening in `;
  const suffix = ', allowing year-on-year comparison.';
  if (years.length === 1) {
    return `${prefix}${years[0]}${suffix}`;
  }
  if (years.length === 2) {
    return `${prefix}both ${years[0]} and ${years[1]}${suffix}`;
  }
  const last = years[years.length - 1];
  const rest = years.slice(0, -1).join(', ');
  return `${prefix}${rest} and ${last}${suffix}`;
}

const RepeatParticipationBlock = ({ data, style }) => {
  const values = data?.repeatParticipantTrendings;
  const hasData = values?.length >= 1 && data?.totalRepeatParticipant != null;
  const sortedValues = useMemo(
    () => (hasData ? [...values].sort((a, b) => a.year - b.year) : []),
    [values, hasData]
  );
  const summaryText = useMemo(() => {
    if (!hasData || sortedValues.length === 0) return '';
    const n = data.totalRepeatParticipant;
    const years = sortedValues.map((v) => v.year);
    return formatYearsSummary(n, years);
  }, [data.totalRepeatParticipant, hasData, sortedValues]);
  const renderDatas = useMemo(() => {
    if (!hasData) return [];
    return sortedValues.map((it) => ({
      value: `${
        typeof it.percent?.formatPercentValue === 'function'
          ? it.percent.formatPercentValue()
          : it.percent
      }%`,
      subValue: ` (${it.total} pax)`,
      title: `${it.year}`,
    }));
  }, [hasData, sortedValues]);

  if (!hasData || !renderDatas.length) return null;

  return (
    <>
      <Box
        mt={3 * SCALE}
        fontSize={14 * SCALE}
        fontWeight={600}
        color="#333"
        style={style}
      >
        Repeat Participation
      </Box>
      <Box
        fontFamily="OpenSans"
        mt={1 * SCALE}
        fontSize={12 * SCALE}
        lineHeight={`${18 * SCALE}px`}
      >
        {summaryText}
      </Box>
      <Box
        mt={2 * SCALE}
        display="flex"
        bgcolor="#E2F2FF"
        width="100%"
        height={64 * SCALE}
        borderRadius={10 * SCALE}
        py={1.5 * SCALE}
      >
        <Grid container>
          {renderDatas.map((it, inx) => (
            <Item
              item
              xs={renderDatas.length >= 3 ? 4 : 6}
              key={`rp-${it.title}-${inx}`}
            >
              <div className="title">{it.title}</div>
              <div className="value">
                {it.value}
                <span className="subValue">{it.subValue}</span>
              </div>
            </Item>
          ))}
        </Grid>
      </Box>
    </>
  );
};

export default RepeatParticipationBlock;
